import { Component } from '@theme/component';

/**
 * <mega-menu-featured>
 *
 * Renders three manually-picked featured products inside a mega menu's
 * featured slot. Products are pre-rendered into the DOM by
 * sections/mega-menu-featured-products.liquid as hidden <template> elements
 * inside #MegaMenuFeaturedData, keyed by collection handle (for hover
 * overrides) and by normalized top-level menu title (for defaults).
 *
 * - On connect, populates the slot with the default set matching this
 *   mega menu's parent link title.
 * - On pointerenter over any descendant link that carries
 *   data-mega-menu-featured-key, swaps the slot to the matching template.
 * - State is sticky by design: the slot is never cleared on pointerleave
 *   so the shopper can move into the featured area to click.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} slot - The <ul> that holds the live cards.
 * @extends {Component<Refs>}
 */
class MegaMenuFeatured extends Component {
  requiredRefs = ['slot'];

  /** @type {HTMLElement | null} */
  #submenu = null;

  connectedCallback() {
    super.connectedCallback();

    this.#submenu = this.closest('.menu-list__submenu-inner') ?? this.closest('.menu-list__submenu');

    this.#populateDefault();

    if (this.#submenu) {
      this.#submenu.addEventListener('pointerenter', this.#onPointerEnter, true);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#submenu) {
      this.#submenu.removeEventListener('pointerenter', this.#onPointerEnter, true);
    }
  }

  #populateDefault = () => {
    const key = this.dataset.parentKey;
    if (!key) return;
    const tpl = document.querySelector(
      `#MegaMenuFeaturedData template[data-mega-menu-featured-default-for="${CSS.escape(key)}"]`
    );
    if (tpl instanceof HTMLTemplateElement) this.#swap(tpl);
  };

  /** @param {Event} event */
  #onPointerEnter = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('[data-mega-menu-featured-key]');
    if (!link || !this.#submenu || !this.#submenu.contains(link)) return;

    const key = /** @type {HTMLElement} */ (link).dataset.megaMenuFeaturedKey;
    if (!key) return;

    const tpl = document.querySelector(
      `#MegaMenuFeaturedData template[data-mega-menu-featured-key="${CSS.escape(key)}"]`
    );
    if (tpl instanceof HTMLTemplateElement) this.#swap(tpl);
  };

  /** @param {HTMLTemplateElement} tpl */
  #swap(tpl) {
    const slot = this.refs.slot;
    if (!slot) return;
    slot.replaceChildren(tpl.content.cloneNode(true));
  }
}

if (!customElements.get('mega-menu-featured')) {
  customElements.define('mega-menu-featured', MegaMenuFeatured);
}
